import { UniqueIdentifier } from "@dnd-kit/core";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { CycleCountsViewsContext } from "@redotech/redo-merchant-inventory-app/inventory-views";
import { CREATE_VIEW_NAME } from "@redotech/redo-views-common/views";
import { SortableTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/sortable-tree";
import { TreeItem } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/types";
import { flattenTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/utilities";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useMemo } from "react";
import {
  FolderWithView,
  treeToViewsAndFolders,
  viewsAndFoldersToTree,
} from "../orders/view-to-tree-utils";

export const CycleCountViews = memo(function CycleCountViews() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { views, folders, openEditFolderModal, updateOrdering } =
    useRequiredContext(CycleCountsViewsContext);
  const urlPrefix = `${routes.inventoryManagementBaseUrl}/intelligent-cycle-counts`;

  const viewsWithoutFolders = useMemo(() => {
    return views?.filter((view) => !view.folderId) || [];
  }, [views]);

  const foldersWithViews = useMemo<FolderWithView<"cycle-counts">[]>(() => {
    return (
      folders?.map((folder) => {
        return {
          folder,
          views: views?.filter((view) => view.folderId === folder._id) || [],
        };
      }) || []
    );
  }, [folders, views]);

  const sortableItems = useMemo(() => {
    return viewsAndFoldersToTree({
      viewsWithoutFolders,
      foldersWithViews,
      urlPrefix,
      viewCounts: {},
      folderCounts: {},
      hideZeroNotifications: false,
    });
  }, [viewsWithoutFolders, foldersWithViews, urlPrefix]);

  const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
    openEditFolderModal(id.toString());
  });

  const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
    const flattenedItems = flattenTree(items);
    const result = treeToViewsAndFolders<"cycle-counts">(
      flattenedItems,
      views || [],
      folders || [],
    );
    updateOrdering(result.views, result.folders);
  });

  return (
    <>
      <SortableTree
        items={sortableItems}
        onRequestEdit={handleRequestEdit}
        setItems={handleUpdateOrdering}
      />
      <CycleCountViewsAddViewItem href={`${urlPrefix}/${CREATE_VIEW_NAME}`} />
      <CycleCountViewsCreateFolderItem />
    </>
  );
});

const CycleCountViewsAddViewItem = memo(function CycleCountViewsAddViewItem({
  href,
}: {
  href: string;
}) {
  const { t } = useTranslation();
  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href }}
      friendlyName={t("+ Add new view", {
        context: "inventory and purchasing navigation label",
      })}
      id="cycle-counts-create-view"
    />
  );
});

const CycleCountViewsCreateFolderItem = memo(
  function CycleCountViewsCreateFolderItem() {
    const { t } = useTranslation();
    const { openCreateFolderModal } = useRequiredContext(
      CycleCountsViewsContext,
    );

    return (
      <RedoSecondaryNavItem
        action={{
          type: "button",
          onClick: () => {
            openCreateFolderModal();
          },
        }}
        friendlyName={t("+ Add new folder", {
          context: "inventory and purchasing navigation label",
        })}
        id="cycle-counts-create-folder"
      />
    );
  },
);
